import {
    ExplisoftService,
    OnboardTenantRequest,
    AuthToken,
    ApiGenericResponse,
    MyInfo,
    User,
    AuthorizationGroupDetails,
    AuthorizationGroup,
    License,
    Tenant,
    RegisterAdminUserFromPublicEmailDomain,
    AuthorizationGroupPublicDetails,
    JoinAuthGroupRequest,
    LicenseBasic,
    Department,
    Designation,
    LevelSuggestions,
    UserDetails,
    UserPatchParam,
    Location,
    LocationRequest,
    LocationUpdateRequest,
    Account,
    AccountRequest,
    AccountType,
    Contact,
    ContactRequest,
    Item,
    ItemRequest,
    ItemStock,
    QuantityCorrection,
    MoveStock,
    Stock,
    ReceiveSupply,
    ReceiveSupplyRequest,
    PurchaseReceiptBasic,
    Config,
    Api,
    Order,
    OrderRequest,
    OrderBasic,
    OrderFilter,
    Project,
    ProjectRequest,
    ProjectContent,
    PropertyLocation,
    PropertyLocationRequest,
    PropertyDetailsResponse,
    Comment,
    Attachment,
    SSOClient
} from './explisoft-service.interface';
import { cache, Key, Policy } from '@services';
import { Socket } from "./socket"
import { Category, Message, LLMRequest, LLMResponse, Batch, TokenStatus, ApiUser, AccessKey } from "@shared"
import { voices } from '@components';

const getHeader = (method: string, payload?: any) => {
    return {
        method: method || 'GET',
        headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
            'x-captcha-token': cache.get<string>(Key.CAPTCHA) || '',
        },
        body: JSON.stringify(payload)
    };
};

export const execute = async <T>(api: Api, payload?: any): Promise<T> => {
    return fetch(api.url, getHeader(api.method, payload))
        .then(async (response) => {
            const isJson = response.headers.get('content-type')?.includes('application/json');
            const data = isJson ? await response.json() : null;
            if (!response.ok) {
                return Promise.reject(data || response.statusText);
            }
            return data as T;
        })
        .catch((error: any) => {
            if (error?.errorCode === 'NOT_AUTHENTICATED') {
                window.location.href = `/auth/login${window.location.search}`;
            }
            throw error;
        });
};

const URL: { [key: string]: Api } = {
    LOGIN: {
        url: '/api/v1/login',
        method: 'POST'
    },
    My_INFO: {
        url: '/api/v1/me',
        method: 'GET'
    },
    BUSINESS_ID_AVAILABILITY: {
        url: '/api/v1/app/domainManagement/check-business-id-availability',
        method: 'POST'
    },
    ADD_BUSINESS_ID: {
        url: '/api/v1/app/domainManagement/add-business-id',
        method: 'POST'
    },
    ADD_USER_IN_DOMAIN: {
        url: '/api/v1/app/users/add-user',
        method: 'POST'
    },
    GET_FILTERED_USERS: {
        url: '/api/v1/app/users/get-filtered-user',
        method: 'GET'
    },
    CREATE_AUTHORIZATION_GROUP: {
        url: '/api/v1/app/authorization/create',
        method: 'POST'
    },
    SEARCH_AUTHORIZATION_GROUP: {
        url: '/api/v1/app/authorization/search',
        method: 'GET'
    },
    GET_AUTHORIZATION_GROUP_DETAILS: {
        url: '/api/v1/app/authorization/groupId',
        method: 'GET'
    },
    GET_AUTHORIZATION_GROUP_USERS: {
        url: '/api/v1/app/authorization/users/groupId',
        method: 'GET'
    },
    ADD_AUTHORIZATION_GROUP_USER: {
        url: '/api/v1/app/authorization/users/add/groupId',
        method: 'POST'
    },
    REMOVE_AUTHORIZATION_GROUP_USER: {
        url: '/api/v1/app/authorization/users/remove/groupId',
        method: 'POST'
    },
    GET_USER_DETAILS: {
        url: '/api/v1/app/users/get-user-details',
        method: 'GET'
    },
    GET_AVAILABLE_LICESNSE: {
        url: '/api/v1/app/appInstall/tenant-installed-licenses',
        method: 'GET'
    },
    UPDATE_AUTHORIZATION_GROUP: {
        url: '/api/v1/app/authorization/groupId',
        method: 'POST'
    },
    FORGOT_PASSWORD: {
        url: '/api/v1/app/users/send-forget-password-email',
        method: 'POST'
    },
    RESET_PASSWORD: {
        url: '/api/v1/app/users/update-user-password',
        method: 'POST'
    },
    SEARCH_TENANT: {
        url: '/api/v1/app/tenantManagement/searchTenant',
        method: 'POST'
    },
    ENABLE_TENANT: {
        url: '/api/v1/app/tenantManagement/enableTenant',
        method: 'POST'
    },
    DISABLE_TENANT: {
        url: '/api/v1/app/tenantManagement/disableTenant',
        method: 'POST'
    },
    ONBOARD_TENANT: {
        url: '/api/v1/app/tenantOnboarding/onboard',
        method: 'POST'
    },
    GET_TENANT_DETAILS: {
        url: '/api/v1/app/tenantManagement/getTenantDetails',
        method: 'GET'
    },
    REGISTER_ADMIN_USER_FROM_PUBLIC_EMAIL_DOMAIN: {
        url: '/api/v1/app/tenantManagement/registerAdminUserFromPublicEmailDomain',
        method: 'POST'
    },
    SET_PUBLIC_URL_SIGNUP: {
        url: '/api/v1/app/authorization/groupId/:groupId/public-url-signup',
        method: 'POST'
    },
    SET_PUBLIC_URL_SIGNUP_APPROVAL_REQUIRED: {
        url: '/api/v1/app/authorization/groupId/:groupId/public-url-signup-approval-required',
        method: 'POST'
    },
    GET_AUTHORIZATION_GROUP_PUBLIC_DETAILS: {
        url: '/api/v1/app/authorization/public/groupId/:groupId',
        method: 'GET'
    },
    JOIN_AUTHORIZATION_GROUP_USING_PUBLIC_LINK: {
        url: '/api/v1/app/authorization/public/join/groupId/:groupId',
        method: 'POST'
    },
    GET_AUTHORIZATION_GROUP_JOIN_REQUESTS: {
        url: '/api/v1/app/authorization/request/join/groupId/:groupId',
        method: 'GET'
    },
    APPROVE_OR_REJECT_AUTHORIZATION_GROUP_JOIN_REQUEST: {
        url: '/api/v1/app/authorization/request/join/groupId/:groupId/action',
        method: 'POST'
    },
    APPS_AVAILABEL_TO_INSTALL: {
        url: '/api/v1/app/appInstall/availableToInstall',
        method: 'GET'
    },
    INSTALLED_APPS: {
        url: '/api/v1/app/appInstall/installed',
        method: 'GET'
    },
    INSTALL_APP: {
        url: '/api/v1/app/appInstall/install',
        method: 'POST'
    },
    UNINSTALL_APP: {
        url: '/api/v1/app/appInstall/uninstall',
        method: 'POST'
    },
    TENANT_INSTALL_APP: {
        url: '/api/v1/app/appInstall/tenant/install',
        method: 'POST'
    },
    TENANT_UNINSTALL_APP: {
        url: '/api/v1/app/appInstall/tenant/uninstall',
        method: 'POST'
    },
    TENANT_INSTALLED_APPS: {
        url: '/api/v1/app/appInstall/tenant/installed',
        method: 'POST'
    },
    TENANT_APPS_AVAILABLE_TO_INSTALL: {
        url: '/api/v1/app/appInstall/tenant/availableToInstall',
        method: 'POST'
    },
    CREATE_DEPARTMENT: {
        url: '/api/v1/app/department/department',
        method: 'POST'
    },
    GET_DEPARTMENTS: {
        url: '/api/v1/app/department/departments?includingInActive=',
        method: 'GET'
    },
    UPDATE_DEPARTMENT: {
        url: '/api/v1/app/department/department/:departmentId',
        method: 'PUT'
    },
    CREATE_DESIGNATION: {
        url: '/api/v1/app/designation/designation',
        method: 'POST'
    },
    GET_DESIGNATIONS: {
        url: '/api/v1/app/designation/designations?includingInActive=',
        method: 'GET'
    },
    UPDATE_DESIGNATION: {
        url: '/api/v1/app/designation/designation/:designationId',
        method: 'PUT'
    },
    GET_LEVEL_SUGGESTIONS: {
        url: '/api/v1/app/designation/level/:level/suggestions',
        method: 'GET'
    },
    UPDATE_USER_DETAILS: {
        url: '/api/v1/app/users/user/:userId',
        method: 'PATCH'
    },
    RENEW_AUTH_TOKEN: {
        url: '/api/v1/renew-token',
        method: 'POST'
    },
    CHANGE_USER_ACTIVE_STATE: {
        url: '/api/v1/app/users/user/:userId/active/:active',
        method: 'POST'
    },
    CREATE_LOCATION: {
        url: '/api/v1/app/locations/location',
        method: 'POST'
    },
    GET_LOCATIONS: {
        url: '/api/v1/app/locations/locations?includingInActive=',
        method: 'GET'
    },
    GET_LOCATION: {
        url: '/api/v1/app/locations/location/:locationId',
        method: 'GET'
    },
    UPDATE_LOCATION: {
        url: '/api/v1/app/locations/location/:locationId',
        method: 'PUT'
    },
    SET_USER_LOCATION: {
        url: '/api/v1/app/locations/user/:userId/location',
        method: 'POST'
    },
    GET_USER_LOCATION: {
        url: '/api/v1/app/locations/user/:userId/location',
        method: 'GET'
    },
    ADD_ACCOUNT: {
        url: '/api/v1/app/accounts/:type',
        method: 'POST'
    },
    SEARCH_ACCOUNTS: {
        url: '/api/v1/app/accounts/:type?q=:query&page=:page',
        method: 'GET'
    },
    UPDATE_ACCOUNT: {
        url: '/api/v1/app/accounts/:type/:accountId',
        method: 'PUT'
    },
    GET_ACCOUNT_CONTACTS: {
        url: '/api/v1/app/accounts/:type/:accountId/contacts',
        method: 'GET'
    },
    CREATE_CONTACT: {
        url: '/api/v1/app/accounts/:type/:accountId/contact',
        method: 'POST'
    },
    UPDATE_CONTACT: {
        url: '/api/v1/app/accounts/:type/:accountId/contact/:contactId',
        method: 'PUT'
    },
    CREATE_ITEM: {
        url: '/api/v1/app/inventory/item',
        method: 'POST'
    },
    GET_ITEMS: {
        url: '/api/v1/app/inventory/items?q=:query&pageNo=:pageNo',
        method: 'GET'
    },
    UPDATE_ITEM: {
        url: '/api/v1/app/inventory/item/:sku',
        method: 'PUT'
    },
    ENABLE_DISABLE_ITEM: {
        url: '/api/v1/app/inventory/item/:sku/state/:state',
        method: 'PUT'
    },
    GET_ITEMS_BY_LOCATION: {
        url: '/api/v1/app/inventory/items/location/:locationId?q=:query&pageNo=:pageNo',
        method: 'GET'
    },
    CORRECT_STOCK_LOCATION: {
        url: '/api/v1/app/inventory/item/:sku/location/:locationId',
        method: 'PUT'
    },
    MOVE_STOCK: {
        url: '/api/v1/app/inventory/item/:sku/move',
        method: 'POST'
    },
    RECEIVE_SUPPLY: {
        url: '/api/v1/app/inventory/receive',
        method: 'POST'
    },
    GET_PURCHASE_RECEIPTS: {
        url: '/api/v1/app/inventory/purchases?supplierId=:supplierId&locationId=:locationId&pageNo=:pageNo',
        method: 'GET'
    },
    GET_PURCHASE_RECEIPT: {
        url: '/api/v1/app/inventory/purchase-receipt/:purchaseReceiptId',
        method: 'GET'
    },
    SET_CONFIG: {
        url: '/api/v1/app/configManagement/config/:app/:key',
        method: 'POST'
    },
    GET_CONFIG: {
        url: '/api/v1/app/configManagement/config/:app/:key',
        method: 'GET'
    },
    CREATE_ORDER: {
        url: '/api/v1/app/sales/order',
        method: 'POST'
    },
    GET_ORDER: {
        url: '/api/v1/app/sales/order/:orderId',
        method: 'GET'
    },
    SEARCH_ORDERS: {
        url: '/api/v1/app/sales/orders?pageNo=:pageNo&locationId=:locationId&customerId=:customerId&userId=:userId',
        method: 'GET'
    },
    EMI_OVERDUE: {
        url: '/api/v1/app/sales/orders/emi/overdue?days=:days',
        method: 'GET'
    },
    RECEIVE_EMI_PAYMENT: {
        url: '/api/v1/app/sales/orders/emi/payment/:emiScheduleId',
        method: 'POST'
    },
    CREATE_PROJECT: {
        url: '/api/v1/app/textImageToVideo/project',
        method: 'POST'
    },
    GET_PROJECTS: {
        url: '/api/v1/app/textImageToVideo/projects?query=:query&pageNo=:pageNo',
        method: 'GET'
    },
    GET_PROJECT_DETAILS: {
        url: '/api/v1/app/textImageToVideo/project/:projectId',
        method: 'GET'
    },
    UPDATE_PROJECT_DETAILS: {
        url: '/api/v1/app/textImageToVideo/project/:projectId',
        method: 'PUT'
    },
    DELETE_PROJECT: {
        url: '/api/v1/app/textImageToVideo/project/:projectId',
        method: 'DELETE'
    },
    GET_PROJECT_CONTENTS: {
        url: '/api/v1/app/textImageToVideo/project/:projectId/contents',
        method: 'GET'
    },
    CREATE_TEXT_CONTENT: {
        url: '/api/v1/app/textImageToVideo/project/:projectId/content',
        method: 'POST'
    },
    UPDATE_TEXT_CONTENT: {
        url: '/api/v1/app/textImageToVideo/project/:projectId/content/:contentId',
        method: 'PUT'
    },
    DELETE_TEXT_CONTENT: {
        url: '/api/v1/app/textImageToVideo/project/:projectId/content/:contentId',
        method: 'DELETE'
    },
    UPLOAD_PROJECT_CONTENT_IMAGE: {
        url: '/api/v1/app/textImageToVideo/project/:projectId/content/:contentId/image',
        method: 'POST'
    },
    GENERATE_VIDEO: {
        url: '/api/v1/app/textImageToVideo/project/:projectId/generate-video',
        method: 'POST'
    },
    REQUEST_IMAGE_GENERATION: {
        url: '/api/v1/app/textImageToVideo/project/:projectId/queue-image-generation',
        method: 'PUT'
    },
    GET_TENANT_VIDEO_QUOTA: {
        url: '/api/v1/app/tenantManagement/videoQuota/:tenantId',
        method: 'GET'
    },
    ADD_TENANT_VIDEO_QUOTA: {
        url: '/api/v1/app/tenantManagement/videoQuota/:tenantId',
        method: 'PUT'
    },
    GET_TENANT_LLM_TOKENS: {
        url: '/api/v1/app/tenantManagement/llmTokens/:tenantId',
        method: 'GET'
    },
    ADD_TENANT_LLM_TOKENS: {
        url: '/api/v1/app/tenantManagement/llmTokens/:tenantId',
        method: 'PUT'
    },
    UPLOAD_MUSIC: {
        url: '/api/v1/app/textImageToVideo/music',
        method: 'POST'
    },
    DELETE_MUSIC: {
        url: '/api/v1/app/textImageToVideo/music/:musicId',
        method: 'DELETE'
    },
    GET_MUSIC: {
        url: '/api/v1/app/textImageToVideo/music',
        method: 'GET'
    },
    UPDATE_PROJECT_BACKGROUND_MUSIC: {
        url: '/api/v1/app/textImageToVideo/project/:projectId/background-music',
        method: 'PUT'
    },
    CHECK_YOUTUBE_CONNECTION: {
        url: '/api/v1/app/textImageToVideo/is-youtube-connected',
        method: 'GET'
    },
    CONNECT_YOUTUBE: {
        url: '/api/v1/app/textImageToVideo/connect-youtube',
        method: 'GET'
    },
    DISCONNECT_YOUTUBE: {
        url: '/api/v1/app/textImageToVideo/disconnect-youtube',
        method: 'GET'
    },
    DOMAIN_LOGO_UPLOAD: {
        url: '/api/v1/app/domainManagement/domain-image',
        method: 'POST'
    },
    SWITCH_TENANT: {
        url: '/api/v1/switch-tenant/:tenantId',
        method: 'POST'
    },
    LLM_SEND: {
        url: '/api/v1/app/ExpliGenie/send',
        method: 'POST'
    },

    ANTI_FAKE_CREATE_TOKENS: {
        url: '/api/v1/app/antifake/generate-tokens',
        method: 'POST'
    },
    ANTI_FAKE_GET_ALL_BATCHES: {
        url: '/api/v1/app/antifake/list-tokens-batches',
        method: 'GET'
    },
    ANTI_FAKE_DOWNLOAD_TOKENS: {
        url: '/api/v1/app/antifake/download/:batchId',
        method: 'GET'
    },
    ANTI_FAKE_IS_ACTIVE: {
        url: '/api/v1/app/antifake/token/is-active?token=:token',
        method: 'GET'
    },
    ANTI_FAKE_VALIDATE: {
        url: '/api/v1/app/antifake/token/validate?token=:token',
        method: 'GET'
    },
    APP_REGISTRATION_REGISTER_API_USER: {
        url: '/api/v1/app/appRegistration/api-user/register',
        method: 'POST'
    },
    APP_REGISTRATION_UPDATE_API_USER_SCOPE: {
        url: '/api/v1/app/appRegistration/api-user/:id/scope/:addOrRemove',
        method: 'PUT'
    },
    APP_REGISTRATION_VIEW_API_USER_SCOPE: {
        url: '/api/v1/app/appRegistration/api-user/:id/scope',
        method: 'GET'
    },
    APP_REGISTRATION_DELETE_API_USER: {
        url: '/api/v1/app/appRegistration/api-user/:id',
        method: 'DELETE'
    },
    APP_REGISTRATION_ROTATE_API_USER_ACCESS_KEY: {
        url: '/api/v1/app/appRegistration/api-user/:id/key/:key',
        method: 'PUT'
    },
    APP_REGISTRATION_GET_API_USERS: {
        url: '/api/v1/app/appRegistration/api-user',
        method: 'GET'
    },
    PROPERTY_SELL_CREATE_LOCATION: {
        url: '/api/v1/app/propertySell/location/:type',
        method: 'POST'
    },
    PROPERTY_SELL_GET_LOCATIONS: {
        url: '/api/v1/app/propertySell/locations/:type',
        method: 'GET'
    },
    PROPERTY_SELL_DELETE_LOCATION: {
        url: '/api/v1/app/propertySell/location/:type/:id',
        method: 'DELETE'
    },
    PROPERTY_SELL_UPDATE_DESCRIPTION: {
        url: '/api/v1/app/propertySell/location/:type/:id/description',
        method: 'PUT'
    },
    PROPERTY_SELL_GET_PROPERTY_DETAILS: {
        url: '/api/v1/app/propertySell/location/property/:id/details',
        method: 'GET'
    },
    PROPERTY_SELL_GENERATE_SALES_PITCH: {
        url: '/api/v1/app/propertySell/location/property/:id/generate-sales-pitch',
        method: 'POST'
    },
    PROPERTY_SELL_SAVE_SALES_PITCH: {
        url: '/api/v1/app/propertySell/location/property/:id/save-sales-pitch',
        method: 'POST'
    },
    ADD_COMMENT: {
        url: '/api/v1/app/commentProvider/addComment/entity/:entityname/id/:entityId',
        method: 'POST'
    },
    GET_COMMENTS: {
        url: '/api/v1/app/commentProvider/getComments/entity/:entityname/id/:entityId',
        method: 'POST'
    },

    UPLOAD_ATTACHMENT: {
        url: '/api/v1/app/attachmentProvider/upload/entity/:entity/id/:id',
        method: 'POST'
    },
    DELETE_ATTACHMENT: {
        url: '/api/v1/app/attachmentProvider/delete/entity/:entity/id/:id/file/:fileName',
        method: 'DELETE'
    },
    GET_ATTACHMENTS: {
        url: '/api/v1/app/attachmentProvider/get/entity/:entity/id/:id',
        method: 'GET'
    },
    SSO_REGISTER_CLIENT: {
        url: '/api/v1/app/sso/client',
        method: 'POST'
    },
    SSO_GET_CLIENTS: {
        url: '/api/v1/app/sso/client',
        method: 'GET'
    },
    SSO_REVOKE_CLIENT: {
        url: '/api/v1/app/sso/client/:id',
        method: 'DELETE'
    }
};

export class ExplisoftServiceImpl implements ExplisoftService {
    private subscribers: { [key: string]: ((message: any) => void)[] } = {};
    constructor() {
        console.log('Intializing ExplisoftServiceImpl...');
        Socket.getInstance().subscribe((message) => {
            const data = JSON.parse(message);
            const category = data.category;
            if (!this.subscribers[category]) {
                return;
            }
            this.subscribers[category].forEach(subscriber => subscriber(data.message));
        });
    }

    public async addPropertyLocation(type: string, location: PropertyLocationRequest): Promise<PropertyLocation> {
        const url = URL.PROPERTY_SELL_CREATE_LOCATION.url.replace(':type', type);
        return await execute<PropertyLocation>({ ...URL.PROPERTY_SELL_CREATE_LOCATION, url }, location);
    }

    public async getPropertyLocations(type: string): Promise<PropertyLocation[]> {
        const url = URL.PROPERTY_SELL_GET_LOCATIONS.url.replace(':type', type);
        return await execute<PropertyLocation[]>({ ...URL.PROPERTY_SELL_GET_LOCATIONS, url });
    }

    public async deletePropertyLocation(type: string, id: string): Promise<void> {
        const url = URL.PROPERTY_SELL_DELETE_LOCATION.url.replace(':type', type).replace(':id', id);
        return await execute<void>({ ...URL.PROPERTY_SELL_DELETE_LOCATION, url });
    }

    public async updatePropertyDescription(type: string, id: string, description: string): Promise<void> {
        const url = URL.PROPERTY_SELL_UPDATE_DESCRIPTION.url.replace(':type', type).replace(':id', id);
        return await execute<void>({ ...URL.PROPERTY_SELL_UPDATE_DESCRIPTION, url }, { description });
    }

    public async getPropertyDetails(id: string): Promise<PropertyDetailsResponse> {
        const url = URL.PROPERTY_SELL_GET_PROPERTY_DETAILS.url.replace(':id', id);
        return await execute<PropertyDetailsResponse>({ ...URL.PROPERTY_SELL_GET_PROPERTY_DETAILS, url });
    }

    public async generateSalesPitch(id: string, prompt?: string): Promise<{ message: string }> {
        const url = URL.PROPERTY_SELL_GENERATE_SALES_PITCH.url.replace(':id', id);
        return await execute<{ message: string }>({ ...URL.PROPERTY_SELL_GENERATE_SALES_PITCH, url }, { prompt });
    }

    public async saveSalesPitch(id: string, type: 'SMALL' | 'LARGE', content: string): Promise<void> {
        const url = URL.PROPERTY_SELL_SAVE_SALES_PITCH.url.replace(':id', id);
        return await execute<void>({ ...URL.PROPERTY_SELL_SAVE_SALES_PITCH, url }, { type, content });
    }

    public async addComment(entityName: string, entityId: string, message: string, parentId?: string): Promise<Comment> {
        const url = URL.ADD_COMMENT.url.replace(':entityname', entityName).replace(':entityId', entityId);
        return await execute<Comment>({ ...URL.ADD_COMMENT, url }, { message, parentId });
    }

    public async getComments(entityName: string, entityId: string, page: number, pageSize: number, parentId?: string): Promise<Comment[]> {
        const url = URL.GET_COMMENTS.url.replace(':entityname', entityName).replace(':entityId', entityId);
        return await execute<Comment[]>({ ...URL.GET_COMMENTS, url }, { page, pageSize, parentId });
    }



    public async uploadAttachment(entity: string, entityId: string, file: File): Promise<{ message: string }> {
        const url = URL.UPLOAD_ATTACHMENT.url.replace(':entity', entity).replace(':id', entityId);
        return await this.uploadFile(file, url, 'file');
    }

    public async getAttachments(entity: string, entityId: string): Promise<Attachment[]> {
        const url = URL.GET_ATTACHMENTS.url.replace(':entity', entity).replace(':id', entityId);
        return await execute<Attachment[]>({ ...URL.GET_ATTACHMENTS, url });
    }

    public async deleteAttachment(entity: string, entityId: string, fileName: string): Promise<void> {
        const url = URL.DELETE_ATTACHMENT.url.replace(':entity', entity).replace(':id', entityId).replace(':fileName', fileName);
        return await execute<void>({ ...URL.DELETE_ATTACHMENT, url });
    }

    public connectYoutube(): Promise<{ redirectUrl: string }> {
        return execute<{ redirectUrl: string }>(URL.CONNECT_YOUTUBE);
    }

    public disconnectYoutube(): Promise<void> {
        return execute<void>(URL.DISCONNECT_YOUTUBE);
    }

    public async checkYoutubeConnection(): Promise<boolean> {
        return (await execute<{ isConnected: boolean }>(URL.CHECK_YOUTUBE_CONNECTION)).isConnected;
    }

    public async requestImageGeneration(projectId: string, contentIds: string[]): Promise<{ message: string }> {
        const url = URL.REQUEST_IMAGE_GENERATION.url.replace(':projectId', projectId);
        return await execute<{ message: string }>({ ...URL.REQUEST_IMAGE_GENERATION, url }, { contentIds });
    }

    public async appRegistrationCreateApiUser(name: string): Promise<ApiUser> {
        return await execute<ApiUser>(URL.APP_REGISTRATION_REGISTER_API_USER, { name });
    }

    public async appRegistrationUpdateApiUserScope(id: string, scope: string, addOrRemove: 'add' | 'remove'): Promise<void> {
        const url = URL.APP_REGISTRATION_UPDATE_API_USER_SCOPE.url.replace(':id', id).replace(':addOrRemove', addOrRemove);
        await execute<void>({ ...URL.APP_REGISTRATION_UPDATE_API_USER_SCOPE, url }, { scope });
    }

    public async appRegistrationDeleteApiUser(id: string): Promise<void> {
        const url = URL.APP_REGISTRATION_DELETE_API_USER.url.replace(':id', id);
        await execute<void>({ ...URL.APP_REGISTRATION_DELETE_API_USER, url });
    }

    public async appRegistrationRotateKey(id: string, key: '1' | '2'): Promise<AccessKey> {
        const url = URL.APP_REGISTRATION_ROTATE_API_USER_ACCESS_KEY.url.replace(':id', id).replace(':key', key);
        return await execute<AccessKey>({ ...URL.APP_REGISTRATION_ROTATE_API_USER_ACCESS_KEY, url });
    }

    public async appRegistrationGetApiUsers(): Promise<ApiUser[]> {
        return await execute<ApiUser[]>(URL.APP_REGISTRATION_GET_API_USERS);
    }

    public async appRegistrationGetApiUserScope(apiUserId: string): Promise<string[]> {
        const url = URL.APP_REGISTRATION_VIEW_API_USER_SCOPE.url.replace(':id', apiUserId);
        return await execute<string[]>({ ...URL.APP_REGISTRATION_VIEW_API_USER_SCOPE, url });
    }

    public async antiFakeIsTokenActive(token: string): Promise<{ active: boolean; }> {
        const url = URL.ANTI_FAKE_IS_ACTIVE.url.replace(':token', token);
        return await execute<{ active: boolean }>({ ...URL.ANTI_FAKE_IS_ACTIVE, url });
    }

    public async antiFakeValidateToken(token: string): Promise<TokenStatus> {
        const url = URL.ANTI_FAKE_VALIDATE.url.replace(':token', token);
        return await execute<TokenStatus>({ ...URL.ANTI_FAKE_VALIDATE, url });
    }

    public async antiFakeCreateTokens(count: number): Promise<{ tokens: string[]; }> {
        return await execute<{ tokens: string[] }>(URL.ANTI_FAKE_CREATE_TOKENS, { count });
    }

    public async antiFakeGetAllBatches(): Promise<{ data: Batch[]; }> {
        return await execute<{ data: Batch[] }>(URL.ANTI_FAKE_GET_ALL_BATCHES);
    }

    public async antiFakeDownloadTokens(batchId: string): Promise<{ tokens: string[] }> {
        const url = URL.ANTI_FAKE_DOWNLOAD_TOKENS.url.replace(':batchId', batchId);
        return await execute<{ tokens: string[] }>({ ...URL.ANTI_FAKE_DOWNLOAD_TOKENS, url });
    }



    public async send(request: LLMRequest): Promise<LLMResponse> {
        return await execute<LLMResponse>(URL.LLM_SEND, request);
    }

    public async createPersonnelWorkspace(workspaceName: string): Promise<AuthToken> {
        return await execute<AuthToken>(URL.CREATE_PERSONNEL_WORKSPACE, { workspaceName });
    }

    public async canCreatePersonnelWorkspace(): Promise<{ allowed: boolean; }> {
        return await execute<{ allowed: boolean }>(URL.CAN_CREATE_PERSONNEL_WORKSPACE);
    }

    public async switchTenant(tenantId: string): Promise<AuthToken> {
        const url = URL.SWITCH_TENANT.url.replace(':tenantId', tenantId);
        const authToken = await execute<AuthToken>({ ...URL.SWITCH_TENANT, url });
        return {
            token: authToken.token,
            expiry: new Date(authToken.expiry),
            tenantId: authToken.tenantId
        };
    }

    public async uploadDomainBrandLogo(file: File): Promise<{ message: string; }> {
        return await this.uploadFile(file, URL.DOMAIN_LOGO_UPLOAD.url, 'logo');
    }

    public subscribeToPushNotifications<C extends Category, M extends Message<C>>(category: C, callback: (message: M) => void) {
        if (!this.subscribers[category]) {
            this.subscribers[category] = [];
        }
        this.subscribers[category].push(callback);
        return () => {
            this.subscribers[category] = this.subscribers[category].filter(subscriber => subscriber !== callback);
        }
    }

    public async updateProjectBackgroundMusic(projectId: string, backgroundMusicId: string, volume: 5): Promise<void> {
        const url = URL.UPDATE_PROJECT_BACKGROUND_MUSIC.url.replace(':projectId', projectId);
        return await execute<void>({ ...URL.UPDATE_PROJECT_BACKGROUND_MUSIC, url }, { backgroundMusicId, volume });
    }

    public async getMusicList(): Promise<{ musicList: { name: string, url: string }[] }> {
        return await execute<{ musicList: { name: string, id: string, url: string }[] }>(URL.GET_MUSIC);
    }

    public async uploadMusic(name: string, file: File): Promise<{ musicUrl: string; }> {
        return await this.uploadFile(file, URL.UPLOAD_MUSIC.url, 'music', name);
    }

    public async deleteMusic(musicId: string): Promise<{ message: string; }> {
        const url = URL.DELETE_MUSIC.url.replace(':musicId', musicId);
        return await execute<{ message: string; }>({ ...URL.DELETE_MUSIC, url });
    }

    public async addTenantVideoQuota(tenantId: string, quota: number): Promise<{ message: string }> {
        const url = URL.ADD_TENANT_VIDEO_QUOTA.url.replace(':tenantId', tenantId);
        return await execute<{ message: string }>({ ...URL.ADD_TENANT_VIDEO_QUOTA, url }, { quota });
    }

    public async getTenantVideoQuota(tenantId: string): Promise<{ quota: number; }> {
        const url = URL.GET_TENANT_VIDEO_QUOTA.url.replace(':tenantId', tenantId);
        return await execute<{ quota: number }>({ ...URL.GET_TENANT_VIDEO_QUOTA, url });
    }

    public async addTenantLLMTokens(tenantId: string, tokens: number): Promise<{ message: string }> {
        const url = URL.ADD_TENANT_LLM_TOKENS.url.replace(':tenantId', tenantId);
        return await execute<{ message: string }>({ ...URL.ADD_TENANT_LLM_TOKENS, url }, { tokens });
    }

    public async getTenantLLMTokens(tenantId: string): Promise<{ tokens: number; }> {
        const url = URL.GET_TENANT_LLM_TOKENS.url.replace(':tenantId', tenantId);
        return await execute<{ tokens: number }>({ ...URL.GET_TENANT_LLM_TOKENS, url });
    }

    public async generateVideo(projectId: string): Promise<{ message: string; }> {
        const url = URL.GENERATE_VIDEO.url.replace(':projectId', projectId);
        return await execute<{ message: string }>({ ...URL.GENERATE_VIDEO, url });
    }

    public async uploadImage(projectId: string, contentId: string, file: File): Promise<{ imageUrl: string }> {
        const url = URL.UPLOAD_PROJECT_CONTENT_IMAGE.url.replace(':projectId', projectId).replace(':contentId', contentId);
        return await this.uploadFile(file, url, 'image');
    }

    private async uploadFile(file: File, url: string, dataName: string, name?: string) {
        const formData = new FormData();
        formData.append(dataName, file);
        if (name) {
            formData.append('name', name);
        }
        return fetch(url, {
            method: 'POST',
            body: formData
        }).then(async (response) => {
            const isJson = response.headers.get('content-type')?.includes('application/json');
            const data = isJson ? await response.json() : null;
            if (!response.ok) {
                return Promise.reject(data || response.statusText);
            }
            return data;
        })
            .catch((error: any) => {
                if (error?.errorCode === 'NOT_AUTHENTICATED') {
                    window.location.href = `/auth/login${window.location.search}`;
                }
                throw error;
            });
    }

    public async getProjectContents(projectId: string): Promise<ProjectContent[]> {
        const url = URL.GET_PROJECT_CONTENTS.url.replace(':projectId', projectId);
        return await execute<ProjectContent[]>({ ...URL.GET_PROJECT_CONTENTS, url });
    }

    public async createProjectContent(projectId: string, content: string, imageText: string): Promise<ProjectContent> {
        const url = URL.CREATE_TEXT_CONTENT.url.replace(':projectId', projectId);
        return await execute<ProjectContent>({ ...URL.CREATE_TEXT_CONTENT, url }, { content, voice: 'A', imageText });
    }

    public async updateProjectContent(projectId: string, contentId: string, content: string, voice: voices): Promise<ProjectContent> {
        const url = URL.UPDATE_TEXT_CONTENT.url.replace(':projectId', projectId).replace(':contentId', contentId);
        return await execute<ProjectContent>({ ...URL.UPDATE_TEXT_CONTENT, url }, { content, voice });
    }

    public async deleteProjectContent(projectId: string, contentId: string): Promise<void> {
        const url = URL.DELETE_TEXT_CONTENT.url.replace(':projectId', projectId).replace(':contentId', contentId);
        return await execute<void>({ ...URL.DELETE_TEXT_CONTENT, url });
    }

    public async createProject(project: ProjectRequest): Promise<Project> {
        return await execute<Project>(URL.CREATE_PROJECT, project);
    };

    public async getProjects(query: string, pageNo: number): Promise<{ hasMore: boolean; projects: Project[]; }> {
        const url = URL.GET_PROJECTS.url.replace(':query', query).replace(':pageNo', pageNo.toString());
        return await execute<{ hasMore: boolean; projects: Project[]; }>({ ...URL.GET_PROJECTS, url });
    }

    public async getProjectDetails(projectId: string): Promise<Project> {
        const url = URL.GET_PROJECT_DETAILS.url.replace(':projectId', projectId);
        return await execute<Project>({ ...URL.GET_PROJECT_DETAILS, url });
    }

    public async updateProjectDetails(projectId: string, project: ProjectRequest): Promise<Project> {
        const url = URL.UPDATE_PROJECT_DETAILS.url.replace(':projectId', projectId);
        return await execute<Project>({ ...URL.UPDATE_PROJECT_DETAILS, url }, project);
    }

    public async deleteProject(projectId: string): Promise<void> {
        const url = URL.DELETE_PROJECT.url.replace(':projectId', projectId);
        return await execute<void>({ ...URL.DELETE_PROJECT, url });
    }


    public async receiveEMIPayment(
        emiScheduleId: string,
        amount: number,
        paymentDate: Date,
        paymentMode: string,
        paymentReference: string
    ): Promise<{ message: string }> {
        const url = URL.RECEIVE_EMI_PAYMENT.url.replace(':emiScheduleId', emiScheduleId);
        return await execute<{ message: string }>(
            {
                ...URL.RECEIVE_EMI_PAYMENT,
                url
            },
            { amount, paymentDate, paymentMode, paymentReference }
        );
    }

    public async emiOverdue(days: number): Promise<{ data: OrderBasic[] }> {
        const url = URL.EMI_OVERDUE.url.replace(':days', days.toString());
        return await execute<{ data: OrderBasic[] }>({ ...URL.EMI_OVERDUE, url });
    }

    public async searchOrders(filter: OrderFilter, pageNo = 1): Promise<{ data: OrderBasic[]; hasMore: boolean }> {
        const url = URL.SEARCH_ORDERS.url
            .replace(':pageNo', pageNo.toString())
            .replace(':locationId', filter.locationId ?? '')
            .replace(':customerId', filter.customerId ?? '')
            .replace(':userId', filter.userId ?? '');
        return await execute<{ data: OrderBasic[]; hasMore: boolean }>({ ...URL.SEARCH_ORDERS, url });
    }

    public async getOrder(orderId: string): Promise<Order> {
        const url = URL.GET_ORDER.url.replace(':orderId', orderId);
        return await execute<Order>({ ...URL.GET_ORDER, url });
    }

    public async createOrder(order: OrderRequest): Promise<Order> {
        return await execute<Order>(URL.CREATE_ORDER, order);
    }

    public async setConfig(app: string, key: string, value: string): Promise<Config> {
        const url = URL.SET_CONFIG.url.replace(':app', app).replace(':key', key);
        return await execute<Config>({ ...URL.SET_CONFIG, url }, { value });
    }

    public async getConfig(app: string, key: string): Promise<Config> {
        const url = URL.GET_CONFIG.url.replace(':app', app).replace(':key', key);
        return await execute<Config>({ ...URL.GET_CONFIG, url });
    }

    public async getPurchaseReceipt(purchaseReceiptId: string): Promise<ReceiveSupply> {
        const url = URL.GET_PURCHASE_RECEIPT.url.replace(':purchaseReceiptId', purchaseReceiptId);
        return await execute<ReceiveSupply>({ ...URL.GET_PURCHASE_RECEIPT, url });
    }

    public async getAllPurchaseReceipts(
        param: { locationId?: string; supplierId?: string },
        page: number
    ): Promise<{ hasMore: boolean; items: PurchaseReceiptBasic[] }> {
        const url = URL.GET_PURCHASE_RECEIPTS.url
            .replace(':locationId', param.locationId ?? '')
            .replace(':supplierId', param.supplierId ?? '')
            .replace(':pageNo', page.toString());
        return await execute<{ hasMore: boolean; items: PurchaseReceiptBasic[] }>({ ...URL.GET_PURCHASE_RECEIPTS, url });
    }

    public async receiveSupply(receiveSupply: ReceiveSupplyRequest): Promise<ReceiveSupply> {
        return await execute<ReceiveSupply>(URL.RECEIVE_SUPPLY, receiveSupply);
    }

    public async moveStock(moveStock: MoveStock): Promise<Stock> {
        const url = URL.MOVE_STOCK.url.replace(':sku', moveStock.sku.toString());
        return await execute<Stock>(
            { ...URL.MOVE_STOCK, url },
            {
                quantity: moveStock.quantity,
                fromLocationId: moveStock.fromLocationId,
                toLocationId: moveStock.toLocationId,
                reason: moveStock.reason
            }
        );
    }

    public async correctStockByLocation(correction: QuantityCorrection): Promise<ItemStock> {
        const url = URL.CORRECT_STOCK_LOCATION.url.replace(':sku', correction.sku.toString()).replace(':locationId', correction.locationId);
        return await execute<ItemStock>(
            { ...URL.CORRECT_STOCK_LOCATION, url },
            { quantityCorrection: correction.quantity, reason: correction.reason }
        );
    }

    public async getAllItemsStockByLocation(
        locationId: string,
        query: string,
        page: number
    ): Promise<{ hasMore: boolean; items: ItemStock[] }> {
        const url = URL.GET_ITEMS_BY_LOCATION.url
            .replace(':locationId', locationId)
            .replace(':query', query)
            .replace(':pageNo', page.toString());
        return await execute<{ hasMore: boolean; items: ItemStock[] }>({ ...URL.GET_ITEMS_BY_LOCATION, url });
    }

    public async enableDisableItem(sku: number, enable: boolean): Promise<Item> {
        const url = URL.ENABLE_DISABLE_ITEM.url.replace(':sku', sku.toString()).replace(':state', enable.toString());
        return await execute<Item>({ ...URL.ENABLE_DISABLE_ITEM, url });
    }

    public async updateItem(sku: number, item: ItemRequest): Promise<Item> {
        const url = URL.UPDATE_ITEM.url.replace(':sku', sku.toString());
        return await execute<Item>({ ...URL.UPDATE_ITEM, url }, item);
    }

    public async getItems(query: string, page = 1): Promise<{ hasMore: boolean; items: Item[] }> {
        const url = URL.GET_ITEMS.url.replace(':query', query).replace(':pageNo', page.toString());
        return await execute<{ hasMore: boolean; items: Item[] }>({ ...URL.GET_ITEMS, url });
    }

    public async createItem(item: ItemRequest): Promise<Item> {
        return await execute<Item>(URL.CREATE_ITEM, item);
    }

    public async getContacts(accountType: AccountType, accountId: string): Promise<Contact[]> {
        const url = URL.GET_ACCOUNT_CONTACTS.url.replace(':type', accountType).replace(':accountId', accountId);
        return await execute<Contact[]>({ ...URL.GET_ACCOUNT_CONTACTS, url });
    }

    public async createContact(accountType: AccountType, accountId: string, contact: ContactRequest): Promise<Contact> {
        const url = URL.CREATE_CONTACT.url.replace(':type', accountType).replace(':accountId', accountId);
        return await execute<Contact>({ ...URL.CREATE_CONTACT, url }, contact);
    }

    public async updateContact(accountType: AccountType, accountId: string, contactId: string, contact: ContactRequest): Promise<Contact> {
        const url = URL.UPDATE_CONTACT.url.replace(':type', accountType).replace(':accountId', accountId).replace(':contactId', contactId);
        return await execute<Contact>({ ...URL.UPDATE_CONTACT, url }, contact);
    }

    public async updateAccount(accountType: AccountType, accountId: string, account: AccountRequest): Promise<Account> {
        const url = URL.UPDATE_ACCOUNT.url.replace(':type', accountType).replace(':accountId', accountId);
        return await execute<Account>({ ...URL.UPDATE_ACCOUNT, url }, account);
    }

    public async getAccounts(accountType: AccountType, query: string, page = 1): Promise<Account[]> {
        const url = URL.SEARCH_ACCOUNTS.url.replace(':type', accountType).replace(':query', query).replace(':page', page.toString());
        return await execute<Account[]>({ ...URL.SEARCH_ACCOUNTS, url });
    }

    public async createAccount(accountType: AccountType, account: AccountRequest): Promise<Account> {
        const url = URL.ADD_ACCOUNT.url.replace(':type', accountType);
        return await execute<Account>({ ...URL.ADD_ACCOUNT, url }, account);
    }

    public async createLocation(location: LocationRequest): Promise<Location> {
        return await execute<Location>(URL.CREATE_LOCATION, location);
    }

    public async getLocation(locationId: string): Promise<Location> {
        const url = URL.GET_LOCATION.url.replace(':locationId', locationId);
        return await execute<Location>({ ...URL.GET_LOCATION, url });
    }

    public async updateLocation(locationId: string, location: LocationUpdateRequest): Promise<Location> {
        const url = URL.UPDATE_LOCATION.url.replace(':locationId', locationId);
        return await execute<Location>({ ...URL.UPDATE_LOCATION, url }, location);
    }

    public async getLocations(includingInActive: boolean): Promise<Location[]> {
        const policy = cache.get<Policy>(Key.POLICY);
        const canFetchAllLocations = policy?.licenseAndScope['locations']?.includes('view_all_locations');
        if (!canFetchAllLocations) {
            const myInfo = cache.get<MyInfo>(Key.USER_INFO);
            if (myInfo.location) {
                return Promise.resolve([myInfo.location]);
            } else {
                return Promise.resolve([]);
            }
        }
        const url = URL.GET_LOCATIONS.url + includingInActive;
        return await execute<Location[]>({ ...URL.GET_LOCATIONS, url });
    }

    public async changeLocationActiveState(locationId: string, active: boolean): Promise<Location> {
        const url = URL.UPDATE_LOCATION.url.replace(':locationId', locationId);
        return await execute<Location>(
            {
                ...URL.UPDATE_LOCATION,
                url
            },
            { isActive: active }
        );
    }

    public async addUserToLocation(locationId: string, userId: string): Promise<{ message: string }> {
        const url = URL.SET_USER_LOCATION.url.replace(':userId', userId);
        return await execute<{ message: string }>(
            {
                ...URL.SET_USER_LOCATION,
                url
            },
            { locationId }
        );
    }

    public async getUserLocation(userId: string): Promise<Location> {
        const url = URL.GET_USER_LOCATION.url.replace(':userId', userId);
        return await execute<Location>({ ...URL.GET_USER_LOCATION, url });
    }

    public async changeUserActiveState(userId: string, active: boolean): Promise<{ message: string }> {
        const url = URL.CHANGE_USER_ACTIVE_STATE.url.replace(':userId', userId).replace(':active', active.toString());
        return await execute<{ message: string }>({ ...URL.CHANGE_USER_ACTIVE_STATE, url });
    }

    public async renewToken(): Promise<AuthToken> {
        const authToken = await execute<AuthToken>(URL.RENEW_AUTH_TOKEN);
        return {
            token: authToken.token,
            expiry: new Date(authToken.expiry),
            tenantId: authToken.tenantId
        };
    }

    public async updateUserDetails(userId: string, patchParam: UserPatchParam): Promise<{ message: string }> {
        const url = URL.UPDATE_USER_DETAILS.url.replace(':userId', userId);
        return await execute<{ message: string }>(
            {
                ...URL.UPDATE_USER_DETAILS,
                url
            },
            patchParam
        );
    }

    public async createDesignation(name: string, description: string, level: number): Promise<Designation> {
        return await execute<Designation>(URL.CREATE_DESIGNATION, { name, description, level });
    }

    public async getDesignations(includingInActive: boolean = false): Promise<Designation[]> {
        return await execute<Designation[]>({
            ...URL.GET_DESIGNATIONS,
            url: URL.GET_DESIGNATIONS.url + includingInActive
        });
    }

    public async changeDesignationActiveState(designation: Designation, active: boolean): Promise<Designation> {
        const url = URL.UPDATE_DESIGNATION.url.replace(':designationId', designation.designationId);
        return await execute<Designation>(
            {
                ...URL.UPDATE_DESIGNATION,
                url
            },
            { name: designation.name, description: designation.description, active, level: designation.level }
        );
    }

    public async updateDesignation(designation: Designation): Promise<Designation> {
        const url = URL.UPDATE_DESIGNATION.url.replace(':designationId', designation.designationId);
        return await execute<Designation>(
            {
                ...URL.UPDATE_DESIGNATION,
                url
            },
            { name: designation.name, description: designation.description, active: designation.active, level: designation.level }
        );
    }

    public async getLevelSuggestions(level: number): Promise<LevelSuggestions> {
        const url = URL.GET_LEVEL_SUGGESTIONS.url.replace(':level', level.toString());
        return await execute<LevelSuggestions>({ ...URL.GET_LEVEL_SUGGESTIONS, url });
    }

    public async updateDepartment(department: Department): Promise<Department> {
        const url = URL.UPDATE_DEPARTMENT.url.replace(':departmentId', department.departmentId);
        return await execute<Department>(
            {
                ...URL.UPDATE_DEPARTMENT,
                url
            },
            { name: department.name, description: department.description, active: department.active }
        );
    }

    public async changeDepartmentActiveState(department: Department, active: boolean): Promise<Department> {
        const url = URL.UPDATE_DEPARTMENT.url.replace(':departmentId', department.departmentId);
        return await execute<Department>(
            {
                ...URL.UPDATE_DEPARTMENT,
                url
            },
            { name: department.name, description: department.description, active }
        );
    }

    public async getDepartments(includingInActive: boolean = false): Promise<Department[]> {
        return await execute<Department[]>({
            ...URL.GET_DEPARTMENTS,
            url: URL.GET_DEPARTMENTS.url + includingInActive
        });
    }

    public async createDepartment(name: string, description: string): Promise<Department> {
        return await execute<Department>(URL.CREATE_DEPARTMENT, { name, description });
    }

    public async installApp(appId: string): Promise<{ message: string }> {
        return await execute<{ message: string }>(URL.INSTALL_APP, { licenseId: appId });
    }

    public async uninstallApp(appId: string): Promise<{ message: string }> {
        return await execute<{ message: string }>(URL.UNINSTALL_APP, { licenseId: appId });
    }

    public async tenantInstallApp(licenseId: string, tenantId: string): Promise<{ message: string }> {
        return await execute<{ message: string }>(URL.TENANT_INSTALL_APP, { licenseId, tenantId });
    }

    public async tenantUninstallApp(licenseId: string, tenantId: string): Promise<{ message: string }> {
        return await execute<{ message: string }>(URL.TENANT_UNINSTALL_APP, { licenseId, tenantId });
    }

    public async tenantInstalledApps(tenantId: string): Promise<LicenseBasic[]> {
        return await execute<LicenseBasic[]>(URL.TENANT_INSTALLED_APPS, { tenantId });
    }

    public async tenantAvailableToInstall(tenantId: string): Promise<LicenseBasic[]> {
        return await execute<LicenseBasic[]>(URL.TENANT_APPS_AVAILABLE_TO_INSTALL, { tenantId });
    }

    public async installedApps(): Promise<LicenseBasic[]> {
        return await execute<LicenseBasic[]>(URL.INSTALLED_APPS);
    }

    public async availableToInstall(): Promise<LicenseBasic[]> {
        return await execute<LicenseBasic[]>(URL.APPS_AVAILABEL_TO_INSTALL);
    }

    public async getAuthorizationGroupJoinRequests(groupId: string): Promise<JoinAuthGroupRequest[]> {
        const url = URL.GET_AUTHORIZATION_GROUP_JOIN_REQUESTS.url.replace(':groupId', groupId);
        return await execute<JoinAuthGroupRequest[]>({ ...URL.GET_AUTHORIZATION_GROUP_JOIN_REQUESTS, url });
    }

    public async approveOrRejectAuthorizationGroupJoinRequest(
        request: JoinAuthGroupRequest,
        approved: boolean
    ): Promise<{ message: string }> {
        const url = URL.APPROVE_OR_REJECT_AUTHORIZATION_GROUP_JOIN_REQUEST.url.replace(':groupId', request.groupId);
        return await execute<{ message: string }>(
            {
                ...URL.APPROVE_OR_REJECT_AUTHORIZATION_GROUP_JOIN_REQUEST,
                url
            },
            { ...request, approved }
        );
    }

    public async joinAuthorizationGroupUsingPublicLink(
        groupId: string,
        firstName: string,
        lastName: string
    ): Promise<{ message: string }> {
        const url = URL.JOIN_AUTHORIZATION_GROUP_USING_PUBLIC_LINK.url.replace(':groupId', groupId);
        return await execute<{ message: string }>(
            {
                ...URL.JOIN_AUTHORIZATION_GROUP_USING_PUBLIC_LINK,
                url
            },
            { firstName, lastName }
        );
    }

    public async getAuthorizationGroupPublicDetails(groupId: string): Promise<AuthorizationGroupPublicDetails> {
        return await execute<AuthorizationGroupPublicDetails>({
            ...URL.GET_AUTHORIZATION_GROUP_PUBLIC_DETAILS,
            url: URL.GET_AUTHORIZATION_GROUP_PUBLIC_DETAILS.url.replace(':groupId', groupId)
        });
    }

    public async setPublicUrlSignup(groupId: string, enable: boolean): Promise<{ message: string }> {
        const url = URL.SET_PUBLIC_URL_SIGNUP.url.replace(':groupId', groupId);
        return await execute<{ message: string }>(
            {
                ...URL.SET_PUBLIC_URL_SIGNUP,
                url
            },
            { groupId, enable }
        );
    }

    public async setPublicUrlSignupApprovalRequired(groupId: string, enable: boolean): Promise<{ message: string }> {
        const url = URL.SET_PUBLIC_URL_SIGNUP_APPROVAL_REQUIRED.url.replace(':groupId', groupId);
        return await execute<{ message: string }>(
            {
                ...URL.SET_PUBLIC_URL_SIGNUP_APPROVAL_REQUIRED,
                url
            },
            { groupId, enable }
        );
    }

    public async registerAdminUserFromPublicEmailDomain(request: RegisterAdminUserFromPublicEmailDomain): Promise<{ message: string }> {
        return await execute<{ message: string }>(URL.REGISTER_ADMIN_USER_FROM_PUBLIC_EMAIL_DOMAIN, request);
    }

    public async searchTenant(query: string): Promise<Tenant[]> {
        return await execute<Tenant[]>(URL.SEARCH_TENANT, { query });
    }

    public async login(emailId: string, password: string): Promise<AuthToken> {
        const authToken = await execute<AuthToken>(URL.LOGIN, { emailId, password });
        return {
            token: authToken.token,
            expiry: new Date(authToken.expiry),
            tenantId: authToken.tenantId
        };
    }

    public async me(): Promise<MyInfo> {
        return await execute<MyInfo>(URL.My_INFO);
    }

    public async checkBusinessIdAvailability(businessId: string): Promise<{ available: boolean }> {
        return await execute<{ available: boolean }>(URL.BUSINESS_ID_AVAILABILITY, { businessId });
    }

    public async addDomainBusinessId(businessId: string): Promise<boolean> {
        await execute<void>(URL.ADD_BUSINESS_ID, { businessId });
        return Promise.resolve(true);
    }

    public async addUserInDomain(
        firstName: string,
        lastName: string,
        emailId: string,
        manager?: string
    ): Promise<{ message: string }> {
        return await execute<{ message: string }>(URL.ADD_USER_IN_DOMAIN, { firstName, lastName, emailId, manager });
    }

    public async getFilteredUser(query: string, showInactive = false): Promise<User[]> {
        return await execute<User[]>({
            ...URL.GET_FILTERED_USERS,
            url: URL.GET_FILTERED_USERS.url + '?q=' + query + '&showInactive=' + showInactive
        });
    }

    public async createAuthorizationGroup(name: string): Promise<AuthorizationGroupDetails> {
        return await execute<AuthorizationGroupDetails>(URL.CREATE_AUTHORIZATION_GROUP, { name });
    }

    public async searchAuthGroup(groupName: string, owner: string, scope: string): Promise<AuthorizationGroup[]> {
        const api = {
            ...URL.SEARCH_AUTHORIZATION_GROUP,
            url: `${URL.SEARCH_AUTHORIZATION_GROUP.url}?groupName=${groupName}&owner=${owner}&scope=${scope}`
        };
        return await execute<AuthorizationGroup[]>(api);
    }

    public async getAuthorizationGroupDetails(groupId: string): Promise<AuthorizationGroupDetails> {
        const api = {
            ...URL.GET_AUTHORIZATION_GROUP_DETAILS,
            url: `${URL.GET_AUTHORIZATION_GROUP_DETAILS.url}/${groupId}`
        };
        return await execute<AuthorizationGroupDetails>(api);
    }

    public async getAuthorizationGroupUsers(groupId: string): Promise<User[]> {
        const api = {
            ...URL.GET_AUTHORIZATION_GROUP_USERS,
            url: `${URL.GET_AUTHORIZATION_GROUP_USERS.url}/${groupId}`
        };
        return await execute<User[]>(api);
    }

    public async addUserInAuthorizationGroup(groupId: string, userId: string): Promise<void> {
        const api = {
            ...URL.ADD_AUTHORIZATION_GROUP_USER,
            url: `${URL.ADD_AUTHORIZATION_GROUP_USER.url}/${groupId}`
        };
        await execute<void>(api, { userId });
    }

    public async removeUserFromAuthorizationGroup(groupId: string, userId: string): Promise<void> {
        const api = {
            ...URL.REMOVE_AUTHORIZATION_GROUP_USER,
            url: `${URL.REMOVE_AUTHORIZATION_GROUP_USER.url}/${groupId}`
        };
        await execute<void>(api, { userId });
    }

    public async getUserDetails(userId: string, withManagerInfo: boolean = false, withMetaInfo: boolean = false): Promise<UserDetails> {
        const api = {
            ...URL.GET_USER_DETAILS,
            url: `${URL.GET_USER_DETAILS.url}/${userId}?withManagerInfo=${withManagerInfo}&withMetaInfo=${withMetaInfo}`
        };
        return await execute<UserDetails>(api);
    }

    public async getAvailableLicense(): Promise<License[]> {
        return await execute<License[]>(URL.GET_AVAILABLE_LICESNSE);
    }

    public async saveAuthGroup(groupId: string, groupName: string, owners: string[], scopes: string[]): Promise<AuthorizationGroupDetails> {
        const api = {
            ...URL.UPDATE_AUTHORIZATION_GROUP,
            url: `${URL.UPDATE_AUTHORIZATION_GROUP.url}/${groupId}`
        };
        return await execute<AuthorizationGroupDetails>(api, { groupName, owners, licenseScopes: scopes });
    }

    public async forgotPassword(emailId: string): Promise<{ message: string }> {
        return await execute<{ message: string }>(URL.FORGOT_PASSWORD, { emailId });
    }

    public async resetPassword(password: string, token: string): Promise<{ message: string }> {
        return await execute<{ message: string }>(URL.RESET_PASSWORD, { password, token });
    }

    public async enableTenant(tenantId: string): Promise<void> {
        return await execute<void>(URL.ENABLE_TENANT, { tenantId });
    }
    public async disableTenant(tenantId: string): Promise<void> {
        return await execute<void>(URL.DISABLE_TENANT, { tenantId });
    }

    public async onboardTenant(request: OnboardTenantRequest): Promise<ApiGenericResponse> {
        return await execute<ApiGenericResponse>(URL.ONBOARD_TENANT, request);
    }

    public async getTenantDetails(tenantId: string): Promise<Tenant> {
        const api = {
            ...URL.GET_TENANT_DETAILS,
            url: `${URL.GET_TENANT_DETAILS.url}/${tenantId}`
        };
        return await execute<Tenant>(api);
    }

    public async registerSsoClient(name: string, redirectUris: string): Promise<SSOClient> {
        return await execute<SSOClient>(URL.SSO_REGISTER_CLIENT, { name, redirectUris });
    }

    public async getSsoClients(): Promise<SSOClient[]> {
        return await execute<SSOClient[]>(URL.SSO_GET_CLIENTS);
    }

    public async revokeSsoClient(clientId: string): Promise<void> {
        const url = URL.SSO_REVOKE_CLIENT.url.replace(':id', clientId);
        return await execute<void>({ ...URL.SSO_REVOKE_CLIENT, url });
    }
}
